/**
 * 専門用語管理画面
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTechnicalTerms,
  createTechnicalTerm,
  updateTechnicalTerm,
  deleteTechnicalTerm,
} from '../../lib/admin-api';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

interface TechnicalTerm {
  id: number;
  term: string;
  reading?: string;
  meaning?: string;
  category?: string;
  general_term?: string;
  priority?: number;
  created: string;
  updated: string;
}

export default function TechnicalTerms() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState<TechnicalTerm | null>(null);
  const [formData, setFormData] = useState({
    term: '',
    reading: '',
    meaning: '',
    category: '',
    general_term: '',
    priority: '5',
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'technical-terms'],
    queryFn: async () => {
      const result = await getTechnicalTerms();
      console.log('専門用語APIレスポンス:', result);
      return result;
    },
  });

  const createMutation = useMutation({
    mutationFn: createTechnicalTerm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'technical-terms'] });
      setIsCreateDialogOpen(false);
      setFormData({ term: '', reading: '', meaning: '', category: '', general_term: '', priority: '5' });
      toast({
        title: '成功',
        description: '専門用語を作成しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || '専門用語の作成に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateTechnicalTerm(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'technical-terms'] });
      setIsEditDialogOpen(false);
      setSelectedTerm(null);
      setFormData({ term: '', reading: '', meaning: '', category: '', general_term: '', priority: '5' });
      toast({
        title: '成功',
        description: '専門用語を更新しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || '専門用語の更新に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTechnicalTerm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'technical-terms'] });
      setIsDeleteDialogOpen(false);
      setSelectedTerm(null);
      toast({
        title: '成功',
        description: '専門用語を削除しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || '専門用語の削除に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const handleCreate = () => {
    if (!formData.term.trim()) {
      toast({
        title: 'エラー',
        description: '専門用語は必須です',
        variant: 'destructive',
      });
      return;
    }
    createMutation.mutate({
      term: formData.term,
      reading: formData.reading || undefined,
      meaning: formData.meaning || undefined,
      category: formData.category || undefined,
      general_term: formData.general_term || undefined,
      priority: parseInt(formData.priority) || 5,
    });
  };

  const handleEdit = (term: TechnicalTerm) => {
    setSelectedTerm(term);
    setFormData({
      term: term.term,
      reading: term.reading || '',
      meaning: term.meaning || '',
      category: term.category || '',
      general_term: term.general_term || '',
      priority: String(term.priority ?? 5),
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = () => {
    if (!selectedTerm || !formData.term.trim()) {
      toast({
        title: 'エラー',
        description: '専門用語は必須です',
        variant: 'destructive',
      });
      return;
    }
    updateMutation.mutate({
      id: selectedTerm.id,
      data: {
        term: formData.term,
        reading: formData.reading || undefined,
        meaning: formData.meaning || undefined,
        category: formData.category || undefined,
        general_term: formData.general_term || undefined,
        priority: parseInt(formData.priority) || 5,
      },
    });
  };

  const handleDelete = (term: TechnicalTerm) => {
    setSelectedTerm(term);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedTerm) {
      deleteMutation.mutate(selectedTerm.id);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900">専門用語管理</h1>
            <p className="text-base text-gray-500 mt-2">医療・歯科・心理などの専門用語の管理</p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm">
                <Plus className="w-4 h-4 mr-1.5" />
                新規作成
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">新規専門用語作成</DialogTitle>
                <DialogDescription className="text-base">新しい専門用語を追加します</DialogDescription>
              </DialogHeader>
              <div className="space-y-5 py-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="create-term" className="text-base font-bold">専門用語（必須）</Label>
                    <Input
                      id="create-term"
                      value={formData.term}
                      onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                      placeholder="例: う蝕"
                      className="h-11 rounded-xl border-gray-200 bg-white mt-2"
                    />
                    <p className="text-sm text-gray-500 mt-1.5">専門用語の名称</p>
                  </div>
                  <div>
                    <Label htmlFor="create-general-term" className="text-base font-bold">一般用語（任意）</Label>
                    <Input
                      id="create-general-term"
                      value={formData.general_term}
                      onChange={(e) => setFormData({ ...formData, general_term: e.target.value })}
                      placeholder="例: 虫歯"
                      className="h-11 rounded-xl border-gray-200 bg-white mt-2"
                    />
                    <p className="text-sm text-gray-500 mt-1.5">対応する一般的な言い方（総評で使用）</p>
                  </div>
                  <div>
                    <Label htmlFor="create-reading" className="text-base font-bold">読み方（任意）</Label>
                    <Input
                      id="create-reading"
                      value={formData.reading}
                      onChange={(e) => setFormData({ ...formData, reading: e.target.value })}
                      placeholder="例: うしょく"
                      className="h-11 rounded-xl border-gray-200 bg-white mt-2"
                    />
                    <p className="text-sm text-gray-500 mt-1.5">専門用語の読み仮名</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="create-category" className="text-base font-bold">カテゴリ（任意）</Label>
                    <Input
                      id="create-category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="例: 歯科、医学、心理"
                      className="h-11 rounded-xl border-gray-200 bg-white mt-2"
                    />
                    <p className="text-sm text-gray-500 mt-1.5">専門用語のカテゴリ分類</p>
                  </div>
                  <div>
                    <Label htmlFor="create-priority" className="text-base font-bold">優先度（1〜10）</Label>
                    <select
                      id="create-priority"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className="w-full h-11 rounded-xl border border-gray-200 bg-white px-3 mt-2 text-base"
                    >
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <option key={n} value={n}>{n}{n === 5 ? '（デフォルト）' : n >= 8 ? '（高）' : n <= 2 ? '（低）' : ''}</option>
                      ))}
                    </select>
                    <p className="text-sm text-gray-500 mt-1.5">総評で指摘される優先順位（高いほど優先）</p>
                  </div>
                </div>
                <div>
                  <Label htmlFor="create-meaning" className="text-base font-bold">意味（任意）</Label>
                  <Textarea
                    id="create-meaning"
                    value={formData.meaning}
                    onChange={(e) => setFormData({ ...formData, meaning: e.target.value })}
                    placeholder="例: 虫歯のこと。歯の硬組織が細菌によって破壊される疾患。"
                    rows={5}
                    className="rounded-xl border-gray-200 bg-white mt-2"
                  />
                  <p className="text-sm text-gray-500 mt-1.5">専門用語の意味や説明</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setIsCreateDialogOpen(false);
                  setFormData({ term: '', reading: '', meaning: '', category: '', general_term: '', priority: '5' });
                }}>
                  キャンセル
                </Button>
                <Button onClick={handleCreate} disabled={createMutation.isPending}>
                  {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '作成'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* 専門用語一覧 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">専門用語一覧</div>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="text-red-600">エラーが発生しました: {error instanceof Error ? error.message : String(error)}</div>
          ) : !data?.terms || data.terms.length === 0 ? (
            <div className="text-center py-8 text-base text-gray-500">
              データがありません。新規作成ボタンから専門用語を追加してください。
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 hover:bg-transparent">
                  <TableHead className="text-base font-bold text-gray-700 text-center">ID</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">専門用語</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">一般用語</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">読み方</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">カテゴリ</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">優先度</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">意味</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">作成日時</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.terms.map((term: TechnicalTerm) => (
                  <TableRow key={term.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{term.id}</TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900">{term.term}</TableCell>
                    <TableCell className="py-4 text-base text-gray-700">{term.general_term || '-'}</TableCell>
                    <TableCell className="py-4 text-base text-gray-700">{term.reading || '-'}</TableCell>
                    <TableCell className="py-4 text-base text-gray-700">{term.category || '-'}</TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{term.priority ?? 5}</TableCell>
                    <TableCell className="py-4 max-w-md text-base text-gray-700">
                      <div className="truncate" title={term.meaning || ''}>
                        {term.meaning || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">
                      {new Date(term.created).toLocaleString('ja-JP')}
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex gap-2 justify-center">
                        <button
                          type="button"
                          onClick={() => handleEdit(term)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                          aria-label="編集"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(term)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                          aria-label="削除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* 編集ダイアログ */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">専門用語編集</DialogTitle>
              <DialogDescription className="text-base">専門用語の情報を編集します</DialogDescription>
            </DialogHeader>
            <div className="space-y-5 py-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="edit-term" className="text-base font-bold">専門用語(必須)</Label>
                  <Input
                    id="edit-term"
                    value={formData.term}
                    onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                    placeholder="例: う蝕"
                    className="h-11 rounded-xl border-gray-200 bg-white mt-2"
                  />
                  <p className="text-sm text-gray-500 mt-1.5">専門用語の名称</p>
                </div>
                <div>
                  <Label htmlFor="edit-general-term" className="text-base font-bold">一般用語(任意)</Label>
                  <Input
                    id="edit-general-term"
                    value={formData.general_term}
                    onChange={(e) => setFormData({ ...formData, general_term: e.target.value })}
                    placeholder="例: 虫歯"
                    className="h-11 rounded-xl border-gray-200 bg-white mt-2"
                  />
                  <p className="text-sm text-gray-500 mt-1.5">対応する一般的な言い方(総評で使用)</p>
                </div>
                <div>
                  <Label htmlFor="edit-reading" className="text-base font-bold">読み方(任意)</Label>
                  <Input
                    id="edit-reading"
                    value={formData.reading}
                    onChange={(e) => setFormData({ ...formData, reading: e.target.value })}
                    placeholder="例: うしょく"
                    className="h-11 rounded-xl border-gray-200 bg-white mt-2"
                  />
                  <p className="text-sm text-gray-500 mt-1.5">専門用語の読み仮名</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-category" className="text-base font-bold">カテゴリ(任意)</Label>
                  <Input
                    id="edit-category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="例: 歯科、医学、心理"
                    className="h-11 rounded-xl border-gray-200 bg-white mt-2"
                  />
                  <p className="text-sm text-gray-500 mt-1.5">専門用語のカテゴリ分類</p>
                </div>
                <div>
                  <Label htmlFor="edit-priority" className="text-base font-bold">優先度(1〜10)</Label>
                  <select
                    id="edit-priority"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full h-11 rounded-xl border border-gray-200 bg-white px-3 mt-2 text-base"
                  >
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <option key={n} value={n}>{n}{n === 5 ? '(デフォルト)' : n >= 8 ? '(高)' : n <= 2 ? '(低)' : ''}</option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-1.5">総評で指摘される優先順位(高いほど優先)</p>
                </div>
              </div>
              <div>
                <Label htmlFor="edit-meaning" className="text-base font-bold">意味(任意)</Label>
                <Textarea
                  id="edit-meaning"
                  value={formData.meaning}
                  onChange={(e) => setFormData({ ...formData, meaning: e.target.value })}
                  placeholder="例: 虫歯のこと。歯の硬組織が細菌によって破壊される疾患。"
                  rows={5}
                  className="rounded-xl border-gray-200 bg-white mt-2"
                />
                <p className="text-sm text-gray-500 mt-1.5">専門用語の意味や説明</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsEditDialogOpen(false);
                setSelectedTerm(null);
                setFormData({ term: '', reading: '', meaning: '', category: '', general_term: '', priority: '5' });
              }}>
                キャンセル
              </Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '更新'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>専門用語の削除</AlertDialogTitle>
              <AlertDialogDescription>
                専門用語「{selectedTerm?.term}」を削除しますか？この操作は取り消せません。
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>キャンセル</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '削除'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
